import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import { deskAt } from './desk-hit';

export interface MapZoomPan {
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  grabbing: boolean;
  canPan: boolean;
  atMin: boolean;
  atMax: boolean;
}

interface Options {
  /** Change whenever the underlying <svg> is replaced, to re-read its viewBox. */
  resetKey?: string;
  min?: number;
  max?: number;
  /** How far one click on the floor zooms in. */
  clickZoom?: number;
}

interface Base {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Drag {
  id: number;
  x: number;
  y: number;
  center: [number, number];
  moved: number;
  captured: boolean;
}

/** Movement, in px, past which a press counts as a pan rather than a click. */
const DRAG_SLOP = 4;

/** The step the + and − buttons zoom by. */
const BUTTON_STEP = 1.4;

const clamp = (v: number, lo: number, hi: number) =>
  lo > hi ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v));
const round = (n: number) => Math.round(n * 100) / 100;

/**
 * Zoom and grab-to-pan for a seat map, driven by the SVG's own viewBox.
 *
 * Zoom is a click, not a gesture: a click on the floor zooms in on that spot,
 * and a click once zoomed in fits the whole floor again. A click on a desk is
 * left to the map, which selects it. There is no wheel or pinch zoom, so the
 * page scrolls over the map like over anything else.
 *
 * Panning the viewBox rather than CSS-transforming the <svg> keeps the artwork
 * crisp at every zoom level, cannot overflow the surrounding card, and leaves
 * the map's own chrome at normal size.
 */
export function useMapZoomPan(
  hostRef: RefObject<HTMLElement | null>,
  { resetKey, min = 1, max = 6, clickZoom = 2.5 }: Options = {},
): MapZoomPan {
  const [zoom, setZoom] = useState(min);
  const [grabbing, setGrabbing] = useState(false);

  const svgRef = useRef<SVGSVGElement | null>(null);
  const baseRef = useRef<Base | null>(null); // the viewBox as authored
  const zoomRef = useRef(min);
  const centerRef = useRef<[number, number] | null>(null); // in user units
  const dragRef = useRef<Drag | null>(null);
  const swallowClickRef = useRef(false);

  /** Write the current zoom/centre back to the viewBox, clamped to the artwork. */
  const apply = useCallback(() => {
    const svg = svgRef.current;
    const base = baseRef.current;
    if (!svg || !base || !centerRef.current) return;
    const z = zoomRef.current;
    const w = base.w / z;
    const h = base.h / z;
    const cx = clamp(centerRef.current[0], base.x + w / 2, base.x + base.w - w / 2);
    const cy = clamp(centerRef.current[1], base.y + h / 2, base.y + base.h - h / 2);
    centerRef.current = [cx, cy];
    svg.setAttribute(
      'viewBox',
      `${round(cx - w / 2)} ${round(cy - h / 2)} ${round(w)} ${round(h)}`,
    );
    // Let the page scroll over the map until there is something to pan.
    svg.style.touchAction = z > min ? 'none' : '';
  }, [min]);

  /** Zoom to `next`, keeping the point under (clientX, clientY) fixed. */
  const zoomTo = useCallback(
    (next: number, clientX: number, clientY: number) => {
      const svg = svgRef.current;
      const base = baseRef.current;
      if (!svg || !base || !centerRef.current) return;
      const z0 = zoomRef.current;
      const z1 = clamp(next, min, max);
      if (z1 === z0) return;

      const rect = svg.getBoundingClientRect();
      const fx = rect.width ? (clientX - rect.left) / rect.width : 0.5;
      const fy = rect.height ? (clientY - rect.top) / rect.height : 0.5;

      const w0 = base.w / z0;
      const h0 = base.h / z0;
      const ux = centerRef.current[0] - w0 / 2 + fx * w0;
      const uy = centerRef.current[1] - h0 / 2 + fy * h0;

      const w1 = base.w / z1;
      const h1 = base.h / z1;
      centerRef.current = [ux - (fx - 0.5) * w1, uy - (fy - 0.5) * h1];
      zoomRef.current = z1;
      setZoom(z1);
      apply();
    },
    [apply, min, max],
  );

  const nudge = useCallback(
    (factor: number) => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      zoomTo(zoomRef.current * factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
    },
    [zoomTo],
  );

  const zoomIn = useCallback(() => nudge(BUTTON_STEP), [nudge]);
  const zoomOut = useCallback(() => nudge(1 / BUTTON_STEP), [nudge]);

  const reset = useCallback(() => {
    const base = baseRef.current;
    if (!base) return;
    zoomRef.current = min;
    centerRef.current = [base.x + base.w / 2, base.y + base.h / 2];
    setZoom(min);
    apply();
  }, [apply, min]);

  useEffect(() => {
    const host = hostRef.current;
    const svg = host?.querySelector('svg') ?? null;
    if (!svg) return undefined;

    const box = (svg.getAttribute('viewBox') ?? '')
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (box.length !== 4 || box.some((n) => !Number.isFinite(n))) return undefined;

    svgRef.current = svg;
    baseRef.current = { x: box[0], y: box[1], w: box[2], h: box[3] };
    zoomRef.current = min;
    centerRef.current = [box[0] + box[2] / 2, box[1] + box[3] / 2];
    setZoom(min);
    apply();

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 || zoomRef.current <= min || !centerRef.current) return;
      dragRef.current = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        center: centerRef.current,
        moved: 0,
        captured: false,
      };
      setGrabbing(true);
      // Capture is deliberately NOT taken here - see onPointerMove.
    };

    const onPointerMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      const base = baseRef.current;
      if (!drag || drag.id !== e.pointerId || !base) return;
      const rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dx = e.clientX - drag.x;
      const dy = e.clientY - drag.y;
      drag.moved = Math.max(drag.moved, Math.hypot(dx, dy));
      // Capture only once this is a real pan. While a pointer is captured the
      // browser retargets its events - and the compatibility mouse events that
      // a click is built from - to the capture element, so capturing on
      // pointerdown made every click report the <svg> as its target, and the
      // map's closest('.desk') lookup found nothing. That is why picking a desk
      // silently stopped working as soon as the map was zoomed in: at the
      // minimum zoom no capture is taken, above it every press took one.
      // Deferring past the slop keeps a drag glued to the pointer without
      // costing a plain click its target.
      if (!drag.captured && drag.moved > DRAG_SLOP) {
        drag.captured = true;
        try {
          svg.setPointerCapture(e.pointerId);
        } catch {
          /* capture is best-effort */
        }
      }
      const w = base.w / zoomRef.current;
      const h = base.h / zoomRef.current;
      centerRef.current = [
        drag.center[0] - dx * (w / rect.width),
        drag.center[1] - dy * (h / rect.height),
      ];
      apply();
    };

    const endDrag = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.id !== e.pointerId) return;
      dragRef.current = null;
      setGrabbing(false);
      // A drag that ends on a desk must not also select it.
      swallowClickRef.current = drag.moved > DRAG_SLOP;
      if (!drag.captured) return;
      try {
        svg.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
    };

    // Bubble-phase on the <svg>: stops the event before it reaches React's
    // listener on the root container, so the map's own onClick never sees it.
    const onClick = (e: MouseEvent) => {
      if (swallowClickRef.current) {
        swallowClickRef.current = false;
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      // A desk is the map's to select, booked or not, so it never zooms.
      if (deskAt(svg, e.clientX, e.clientY)) return;
      if (zoomRef.current > min) reset();
      else zoomTo(min * clickZoom, e.clientX, e.clientY);
    };

    svg.addEventListener('pointerdown', onPointerDown);
    svg.addEventListener('pointermove', onPointerMove);
    svg.addEventListener('pointerup', endDrag);
    svg.addEventListener('pointercancel', endDrag);
    svg.addEventListener('click', onClick);
    // An un-captured press released off the map still has to end the drag, or
    // the grab cursor sticks. The <svg> listeners run first and clear the drag,
    // so these only ever fire for a release the map itself never saw.
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    return () => {
      svg.removeEventListener('pointerdown', onPointerDown);
      svg.removeEventListener('pointermove', onPointerMove);
      svg.removeEventListener('pointerup', endDrag);
      svg.removeEventListener('pointercancel', endDrag);
      svg.removeEventListener('click', onClick);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      svg.style.touchAction = '';
      dragRef.current = null;
      svgRef.current = null;
    };
  }, [hostRef, resetKey, min, clickZoom, apply, zoomTo, reset]);

  return {
    zoom,
    zoomIn,
    zoomOut,
    reset,
    grabbing,
    canPan: zoom > min,
    atMin: zoom <= min,
    atMax: zoom >= max,
  };
}
